import { useCallback, useRef, useEffect } from "react";
import { Button, Card, CardContent, Skeleton } from "@repo/ui";
import { AlertTriangle, PackageOpen, SearchX } from "lucide-react";
import { ProductCard, type ProductCardData } from "./product-card";
import type { ConditionCriteria } from "@/lib/conditions";

const SKELETON_DELAYS = [0, 0.4, 0.8] as const;

export type EmptyReason =
  | "no-products"
  | "no-matches"
  | "all-out-of-stock"
  | "store-unavailable";

interface ProductGridProps {
  products: ProductCardData[];
  onProductClick: (product: ProductCardData) => void;
  showPrice?: boolean;
  /** When false, hides Vendor badges on every card. Driven by catalog `hideVendorsFromCreators`. */
  showVendor?: boolean;
  /** When false, hides Tag badges on every card. Driven by catalog `hideTagsFromCreators`. */
  showTags?: boolean;
  hasMore?: boolean;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  isError?: boolean;
  onRetry?: () => void;
  /** Short hash code to share with support for error correlation. */
  supportCode?: string | null;
  /** Why the product list is empty — drives contextual empty-state messaging. */
  emptyReason?: EmptyReason;
  /** Condition criteria for badge highlighting on product cards. */
  conditionCriteria?: ConditionCriteria;
  /** Lookup of total cart quantity per product ID (across all variants). */
  cartQuantityByProductId?: Map<number, number>;
  /** Called when the trash icon on a product card is clicked to remove from cart. */
  onRemoveFromCart?: (product: ProductCardData) => void;
  /** Minimum stock level from brand inventory settings. Cards with inventory <= this threshold show as OOS. */
  minimumStockLevel?: number;
}

export function ProductGrid({
  products,
  onProductClick,
  showPrice = true,
  showVendor = true,
  showTags = true,
  hasMore = false,
  isLoading = false,
  isFetchingMore = false,
  onLoadMore,
  isError = false,
  onRetry,
  supportCode,
  emptyReason = "no-products",
  conditionCriteria,
  cartQuantityByProductId,
  onRemoveFromCart,
  minimumStockLevel = 0,
}: ProductGridProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry?.isIntersecting &&
        hasMore &&
        !isLoading &&
        !isFetchingMore &&
        onLoadMore
      ) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, isFetchingMore, onLoadMore],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "100px",
      threshold: 0,
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  if (isError && products.length === 0) {
    return (
      <div role="alert" className="text-center py-12 space-y-3">
        <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
        <p className="text-muted-foreground">Unable to load products</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
        {supportCode && (
          <p className="text-xs text-muted-foreground/60 font-mono select-all">
            Reference: {supportCode}
          </p>
        )}
      </div>
    );
  }

  if (products.length === 0 && !isLoading && !isFetchingMore && !hasMore) {
    if (emptyReason === "store-unavailable") {
      return (
        <div className="text-center py-12 space-y-2">
          <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground font-medium">
            Product catalog is no longer available
          </p>
          <p className="text-sm text-muted-foreground/70">
            The connected store may have been disconnected. Please contact the
            brand for assistance.
          </p>
          {supportCode && (
            <p className="text-xs text-muted-foreground/60 font-mono select-all">
              Reference: {supportCode}
            </p>
          )}
        </div>
      );
    }

    if (emptyReason === "all-out-of-stock") {
      return (
        <div className="text-center py-12 space-y-2">
          <PackageOpen className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground font-medium">
            All products are currently out of stock
          </p>
          <p className="text-sm text-muted-foreground/70">
            Please check back later or contact the brand for availability.
          </p>
        </div>
      );
    }

    if (emptyReason === "no-matches") {
      return (
        <div className="text-center py-12 space-y-2">
          <SearchX className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground font-medium">
            No products match your search
          </p>
          <p className="text-sm text-muted-foreground/70">
            Try a different search term or clear your filters.
          </p>
        </div>
      );
    }

    return (
      <div className="text-center py-12 space-y-2">
        <PackageOpen className="h-8 w-8 text-muted-foreground mx-auto" />
        <p className="text-muted-foreground font-medium">
          No products available
        </p>
        <p className="text-sm text-muted-foreground/70">
          This catalog doesn't have any products yet. Please contact the brand.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {products.map((product) => (
          <ProductCard
            key={`${product.id}-${product.catalogProductId || ""}`}
            product={product}
            onClick={onProductClick}
            showPrice={showPrice}
            showVendor={showVendor}
            showTags={showTags}
            conditionCriteria={conditionCriteria}
            cartQuantity={cartQuantityByProductId?.get(product.id)}
            onRemoveFromCart={onRemoveFromCart}
            minimumStockLevel={minimumStockLevel}
          />
        ))}

        {/* Initial load: 8 skeleton cards inline in the grid */}
        {isLoading &&
          Array.from({ length: 8 }).map((_, i) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton items
            <Card key={`skeleton-${i}`} className="overflow-hidden">
              <Skeleton className="aspect-square w-full" />
              <CardContent className="p-3">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}

        {/* Load-more wave: 3 cards pulsing 1…2…3 inline in the grid */}
        {isFetchingMore &&
          SKELETON_DELAYS.map((delay, i) => (
            <Card
              // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton items
              key={`wave-${i}`}
              className="overflow-hidden"
              style={{
                animation: "grid-wave 1.2s ease-in-out infinite",
                animationDelay: `${delay}s`,
              }}
            >
              <div className="aspect-square w-full bg-muted" />
              <CardContent className="p-3">
                <div className="h-4 w-full rounded bg-muted mb-2" />
                <div className="h-4 w-20 rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
      </div>

      {/* Sentinel for IntersectionObserver auto-load */}
      {(hasMore || onLoadMore) && <div ref={loadMoreRef} className="h-4" />}
    </div>
  );
}
